import React, { SyntheticEvent, useEffect, useMemo, useRef, useState } from 'react';
import { Company, CompanyType, useMerchantsLazyQuery } from '../../graphql/types';
import { debounce, delay } from 'lodash';
import { Autocomplete, Button, ClickAwayListener, Dialog, Paper, Stack, TextField } from '@mui/material';
import AssetLogo from '../AssetLogo';
import AssetListButtonOption from '../AssetListButtonOption';
import { useFormik } from 'formik';
import { Values } from './AddMerchantCashAdvance';
import { PlusCircleOutlined } from '@ant-design/icons';
import AddCompany from './AddCompany';
import ActionConfirmation from '../../components/mca/ActionConfirmation';
import { capitalizeFirstLetter } from 'utils/strings';

export interface Props {
  id: string;
  disabled: boolean;
  formik: ReturnType<typeof useFormik<Values>>;
}

export const MerchantAutocomplete = ({ id, disabled, formik }: Props) => {
  const { errors, touched, values, handleBlur, setFieldValue } = formik;
  const [merchants, setMerchants] = useState<readonly Company[]>([]);
  const [showInfo, setShowInfo] = useState<boolean>(disabled || !!values?.merchant?.id);
  const inputReference = useRef<HTMLInputElement>(null);
  const [hasInitialFetch, setHasInitialFetch] = useState<boolean>(false);
  const [getMerchants, { loading: merchantsLoading }] = useMerchantsLazyQuery({
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      setMerchants((data.merchants.data as Company[]) || []);
    }
  });
  const [openDrawerConfirmation, setOpenDrawerConfirmation] = useState<boolean>(false);
  const handleClose = () => {
    setOpenDrawerConfirmation(true);
  };

  const merchantsFetch = useMemo(
    () =>
      debounce((input: string) => {
        getMerchants({ variables: { search: input } });
      }, 200),
    [getMerchants]
  );

  const [merchantInputValue, setMerchantInputValue] = useState<string>('');

  useEffect(() => {
    merchantsFetch(merchantInputValue);
  }, [merchantInputValue, merchantsFetch]);

  const [isMerchantModalShown, showMerchantModal] = useState<boolean>(false);
  const handleAdd = () => {
    showMerchantModal(!isMerchantModalShown);
  };
  const handleInputClick = () => {
    if (!hasInitialFetch) {
      merchantsFetch('');
      setHasInitialFetch(true);
    }
  };
  const PaperComponentCustom = (options: any) => {
    const { containerProps, children } = options;
    return (
      <Paper {...containerProps}>
        <Stack>
          {children}
          <Button
            variant="outlined"
            color="primary"
            startIcon={<PlusCircleOutlined />}
            onMouseDown={(event) => {
              event.preventDefault();
              handleAdd();
            }}
          >
            Add new Merchant company
          </Button>
        </Stack>
      </Paper>
    );
  };

  return (
    <>
      <Dialog maxWidth="sm" fullWidth onClose={handleClose} open={isMerchantModalShown} sx={{ '& .MuiDialog-paper': { p: 0 } }}>
        {isMerchantModalShown && (
          <AddCompany
            open={isMerchantModalShown}
            entity={null}
            companyTypes={[CompanyType.Merchant]}
            onCancel={handleClose}
            onSave={(merchant) => { 
              setFieldValue('merchant', merchant);
              showMerchantModal(false);
            }}

          />
        )}
      </Dialog>
      {values?.merchant?.id && (showInfo || disabled) && (
        <Stack direction={'row'}>
          <AssetListButtonOption
            logo={values?.merchant?.logo || null}
            name={values?.merchant?.legalName || ''}
            type={`${values?.merchant?.type?.map(t => capitalizeFirstLetter(t)).join(' / ') || ''} Company`}
            showEditAction={!disabled}
            onClick={() => {
              if (disabled) return;
              setShowInfo(false);
              delay(() => inputReference.current?.focus(), 500);
            }}
          />
        </Stack>
      )}
      {!showInfo && !disabled && (
        <ClickAwayListener
          onClickAway={() => {
            setShowInfo(disabled ? false : !!values?.merchant?.id);
          }}
        >
          <Autocomplete
            PaperComponent={PaperComponentCustom}
            disabled={disabled}
            id={id}
            getOptionLabel={(option: Company) => option.legalName || ''}
            options={merchants}
            autoComplete
            value={(values.merchant?.id ? values.merchant as Company : null)}
            isOptionEqualToValue={(option: Company, value: Company) =>
              !!value?.id && option.id === value.id
            }            
            onChange={(event: SyntheticEvent, newValue: Company | null) => {
              setFieldValue('merchant', newValue || null);
              if (!newValue) {
                setMerchantInputValue('');
              }
              setShowInfo(!!newValue?.id);
            }}
            onInputChange={(event: SyntheticEvent, newInputValue: string) => {
              setMerchantInputValue(newInputValue);
            }}

            renderInput={(params) => (
              <TextField
                {...params}
                fullWidth
                placeholder={'Select Merchant'}
                inputRef={inputReference}
                name="merchant"
                onClick={handleInputClick}
                onBlur={handleBlur}
                error={Boolean(touched.merchant?.id && errors.merchant?.id)}
                helperText={touched.merchant?.id && errors.merchant?.id}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: values?.merchant?.id && (
                    <AssetLogo logo={values?.merchant?.logo} name={values?.merchant?.legalName} size={'sm'} />
                  )
                }}
                sx={{
                  '& fieldset': { border: 'none' },
                  '& .MuiInputLabel-root': { padding: '14px 0 8px 16px' },
                  '& .MuiOutlinedInput-root': { padding: '14px 0 8px 16px' }
                }}
              />
            )}
            renderOption={(props, option) => {
              const { key, ...rest } = props;
              return (
                <AssetListButtonOption key={key} logo={option.logo || null} name={option.legalName || ''} type={`${option.type?.map(t => capitalizeFirstLetter(t)).join(' / ') || ''} Company`} {...rest} />
              );
            }}
          />
        </ClickAwayListener>
      )}
      {openDrawerConfirmation && (
        <ActionConfirmation
          title={'The form will close and changes will be lost. Are you sure you want to proceed?'}
          open={openDrawerConfirmation}
          useYesNoButtonLabels={true}
          type="error"
          handleClose={(approved: boolean) => {
            setOpenDrawerConfirmation(false);
            if (!approved) {
              return;
            }
            showMerchantModal(false);
          }}
        />
      )}
    </>
  );
};
